import React, {
    FC,
    PropsWithChildren,
    useCallback,
    useEffect,
    useMemo,
    useRef,
} from 'react';

import { Platform, Pressable, StyleProp, View, ViewStyle } from 'react-native';

import { useTranslation } from 'react-i18next';
import { SvgProps } from 'react-native-svg';

import { LANGUAGE_KEYS } from 'config/languageKeys';

import { MenuConfigurationItem } from 'store/railsBuilder/reducer.types';

import setAccessibilityFocusTo from 'services/accessibility/setAccessibilityFocusTo';

import { COLOR } from 'utils/colors';
import isFunction from 'utils/function';
import { isWeb } from 'utils/responsiveness';

import Text, { ETextCategory, ETextColor } from 'components/Text';

import { SimpleSettingsItem } from '../../types';
import styles from './styles';

const {
    ACCESSIBILITY: {
        SETTINGS: {
            ACCESSIBILITY_SETTINGS_ITEM_LIST,
            ACCESSIBILITY_SETTINGS_CHECKMARK,
        },
        ACCESSIBILITY_GENERAL_TAPP_TWICE,
        ACCESSIBILITY_GENERAL_TAPP_TWICE_WEB,
    },
} = LANGUAGE_KEYS;

type Props<T = MenuConfigurationItem | SimpleSettingsItem> = {
    item: T;
    title: string;
    subtitle?: string;
    rightTitle?: string;
    disabled?: boolean;
    Icon?: FC<SvgProps>;
    RightIcon?: FC<SvgProps>;
    onPress?(item: T): void;
    style?: StyleProp<ViewStyle>;
    titleContainerStyle?: StyleProp<ViewStyle>;
    rightTitleStyle?: StyleProp<ViewStyle>;
    testID?: string;
    titleColor?: ETextColor | undefined;
    position?: number;
    total?: number;
    isSelected?: boolean;
};

const SettingsRowItem = ({
    item,
    title,
    subtitle,
    rightTitle,
    disabled = false,
    Icon,
    RightIcon,
    onPress,
    style,
    titleContainerStyle,
    rightTitleStyle,
    testID,
    titleColor = ETextColor.neutral,
    position,
    total,
    isSelected,
}: Props) => {
    const { t } = useTranslation();
    const rowItemRef = useRef<View | null>(null);

    const handlePress = useCallback(() => {
        if (isFunction(onPress)) {
            onPress(item);
        }
    }, [onPress, item]);

    const accessibilityLabel = useMemo(() => {
        if (position && total) {
            const baseLabel = `${t(ACCESSIBILITY_SETTINGS_ITEM_LIST, {
                number: position,
                total,
                interpolation: {
                    prefix: '%',
                    suffix: '%',
                    escapeValue: false,
                },
            })} ${title}${
                isWeb && !disabled
                    ? ` ${t(ACCESSIBILITY_GENERAL_TAPP_TWICE_WEB)}`
                    : ''
            }`;

            if (isSelected) {
                return `${t(ACCESSIBILITY_SETTINGS_CHECKMARK)} ${baseLabel} `;
            }

            return baseLabel;
        }

        return title;
    }, [position, total, title, t, disabled, isSelected]);

    const Container = ({ children }: PropsWithChildren<{}>) =>
        disabled ? (
            <View
                ref={rowItemRef}
                style={[styles.container, style]}
                // eslint-disable-next-line react/jsx-props-no-spreading
                {...(isWeb && { tabIndex: 0 })}
                testID={testID}
                accessibilityLabel={accessibilityLabel}
                accessible
            >
                {children}
            </View>
        ) : (
            <Pressable
                ref={rowItemRef}
                style={[styles.container, style]}
                disabled={disabled}
                // @ts-ignore
                tabIndex={0}
                onPress={handlePress}
                testID={testID}
                accessibilityLabel={accessibilityLabel}
                {...(Platform.OS === 'ios' && {
                    accessibilityHint: t(ACCESSIBILITY_GENERAL_TAPP_TWICE),
                })}
                accessible
            >
                {children}
            </Pressable>
        );

    useEffect(() => {
        if (position === 1) {
            setTimeout(() => {
                setAccessibilityFocusTo(rowItemRef);
            }, 300);
        }
    }, [position]);

    return (
        <Container>
            {!!Icon && (
                <View style={styles.icon}>
                    <Icon />
                </View>
            )}
            <View style={[styles.titleContainer, titleContainerStyle]}>
                <Text
                    style={styles.title}
                    category={ETextCategory.body1}
                    color={titleColor}
                    accessibilityRole="text"
                >
                    {title}
                </Text>
                {!!subtitle && (
                    <Text
                        style={styles.subtitle}
                        category={ETextCategory.body2}
                        color={ETextColor.light_grey}
                        accessibilityRole="text"
                        accessibilityLabel={subtitle}
                    >
                        {subtitle}
                    </Text>
                )}
            </View>
            {!!RightIcon && (
                <RightIcon fill={COLOR.NEUTRAL} style={styles.arrow} />
            )}
            {!!rightTitle && (
                <Text
                    style={[styles.rightSubtitle, rightTitleStyle]}
                    category={ETextCategory.body1}
                    color={ETextColor.light_grey}
                    numberOfLines={2}
                    accessibilityRole="text"
                    accessibilityLabel={rightTitle}
                >
                    {rightTitle}
                </Text>
            )}
        </Container>
    );
};

export default SettingsRowItem;
